import React, { useState } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlay, FiX } = FiIcons;

const FeaturedEpisode = () => {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = () => {
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setIsModalOpen(false);
    document.body.style.overflow = 'unset';
  };

  return (
    <>
      <section
        id="featured-episode"
        className="section py-16 sm:py-20 md:py-24 bg-black text-white relative"
        ref={ref}
      >
        {/* Vignette Effect */}
        <div className="absolute inset-0 bg-gradient-radial from-transparent via-black/50 to-black"></div>

        <div className="container relative z-10 px-4">
          <motion.div
            className="text-center max-w-4xl mx-auto"
            initial={{ y: 50, opacity: 0 }}
            animate={isInView ? { y: 0, opacity: 1 } : {}}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl sm:text-4xl md:text-6xl font-bold mb-4 sm:mb-6">
              See the Energy. <span className="text-red-500 border-b-2 sm:border-b-4 border-red-500">Feel the Honesty.</span>
            </h2>

            <p className="text-lg sm:text-xl md:text-2xl text-gray-300 mb-8 sm:mb-12 leading-relaxed px-2">
              Watch the latest episode and step inside the conversations that move people. It's not interviews — it's impact. Every guest brings truth, laughter, and lessons that hit home.
            </p>

            {/* Video Thumbnail with Play Button */}
            <motion.div
              className="relative aspect-video rounded-xl sm:rounded-2xl shadow-2xl mb-6 sm:mb-8 overflow-hidden border-4 border-red-500 cursor-pointer group"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={isInView ? { scale: 1, opacity: 1 } : {}}
              transition={{ duration: 0.8, delay: 0.3 }}
              onClick={openModal}
            >
              {/* Thumbnail Image */}
              <img
                src="https://img.youtube.com/vi/CSl2zhkrSww/maxresdefault.jpg"
                alt="Boss At Heart Podcast - Latest Episode"
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              />

              {/* Dark Overlay */}
              <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors"></div>

              {/* Recording Badge */}
              <div className="absolute top-4 right-4 bg-red-600 text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-2 animate-pulse">
                <div className="w-2 h-2 bg-white rounded-full"></div>
                REC
              </div>

              {/* Play Button */}
              <div className="absolute inset-0 flex items-center justify-center">
                <motion.div
                  className="w-20 h-20 sm:w-24 sm:h-24 bg-red-500 rounded-full flex items-center justify-center shadow-2xl group-hover:bg-red-600 transition-colors"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <SafeIcon icon={FiPlay} className="text-white text-3xl sm:text-4xl ml-1" />
                </motion.div>
              </div>

              {/* Hover Text */}
              <div className="absolute bottom-4 left-4 right-4 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                <p className="text-sm sm:text-base font-semibold">Click to watch full episode</p>
              </div>
            </motion.div>

            {/* CTA Button */}
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={isInView ? { y: 0, opacity: 1 } : {}}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <a
                href="https://www.youtube.com/@camgocrazy_bossatheart/videos"
                target="_blank"
                rel="noopener noreferrer"
                className="btn text-base sm:text-lg px-6 sm:px-8 py-3 sm:py-4"
              >
                <SafeIcon icon={FiPlay} />
                Watch Full Episodes
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Video Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/95"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
          >
            {/* Close Button */}
            <motion.button
              className="absolute top-4 right-4 w-12 h-12 bg-red-500 rounded-full flex items-center justify-center text-white hover:bg-red-600 transition-colors z-10"
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              exit={{ scale: 0, rotate: 180 }}
              transition={{ type: "spring", duration: 0.5 }}
              onClick={closeModal}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <SafeIcon icon={FiX} className="text-2xl" />
            </motion.button>

            {/* Video Container */}
            <motion.div
              className="w-full max-w-5xl aspect-video bg-black rounded-xl overflow-hidden shadow-2xl"
              initial={{ scale: 0.8, y: 50 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.8, y: 50 }}
              transition={{ type: "spring", duration: 0.5 }}
              onClick={(e) => e.stopPropagation()}
            >
              <iframe
                width="100%"
                height="100%"
                src="https://www.youtube.com/embed/CSl2zhkrSww?si=mXV8r7rBsbqwYuIp&autoplay=1&controls=1&showinfo=0&rel=0"
                title="Boss At Heart Podcast - Latest Episode"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
                className="w-full h-full"
              />
            </motion.div>

            {/* Click outside hint */}
            <motion.p
              className="absolute bottom-8 text-white/60 text-sm"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              Click outside or press ESC to close
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default FeaturedEpisode;